import { bisect, max, range } from 'd3-array';
import { scaleLinear, scalePoint } from 'd3-scale';
import { pointer, select, Selection } from 'd3-selection';
import { area, curveMonotoneX, line } from 'd3-shape';

import { Dictionary } from '../../../models';

import { AbstractComponent, Config as BaseConfig, Params, Size } from '../abstract.component';

export class AreaChartComponent extends AbstractComponent<Config, Data> {

  private static COMPONENT_CLASS = 'epb-area-chart';
  private static COMPONENT_TEMPLATE = `<svg class="${ AreaChartComponent.COMPONENT_CLASS }" shape-rendering="geometricprecision"></svg>`;

  private static X_AXIS_HEIGHT = 30;
  private static Y_AXIS_WIDTH = 60;
  private static Y_AXIS_STEPS = 4;
  private static AXIS_TEXT_WIDTH = 50;

  private static POINT_RADIUS = 4;

  private static TOOLTIP_TRANSITIONS_DURATION_IN_MILLIS = 200;

  private size: Size;

  private element: Selection;

  private scaleX = scalePoint();
  private scaleY = scaleLinear();

  constructor(containerEl: HTMLElement, params: Params<Config, Data> = {}) {
    super(containerEl, params);

    containerEl.innerHTML = AreaChartComponent.COMPONENT_TEMPLATE;
    this.size = this.getElementSize(containerEl);

    this.element = this.containerElement
      .select('svg');

    this.updateScales();

    this.renderAxis();

    this.renderData();

    this.renderTooltip();
  }

  public update(params: Params<Config, Data>): void {
    super.update(params);

    this.updateScales();

    this.renderAxis();

    this.renderData();

    this.renderTooltip();
  }

  protected getDefaultConfig(): Config {
    return Object.assign(super.getDefaultConfig(), {
      values: []
    });
  }

  protected get data(): Data {
    return (super.data || []); // Default value: empty array
  }

  protected set data(data: Data) {
    super.data = data;
  }

  protected resize(containerElSize: Size): void {
    this.size = containerElSize;

    this.updateScales();

    this.renderAxis(0);

    this.renderData(0);

    this.renderTooltip();
  }

  private updateScales(): void {
    this.scaleX
      .range([AreaChartComponent.Y_AXIS_WIDTH, (this.size.width - (AreaChartComponent.AXIS_TEXT_WIDTH / 2))])
      .domain(this.data.map((dataItem: DataItem) => dataItem.id));

    this.scaleY
      .range([(this.size.height - AreaChartComponent.X_AXIS_HEIGHT), (AreaChartComponent.X_AXIS_HEIGHT / 2)])
      .domain([0, this.getDataMaxValue()]);
  }

  private getDataMaxValue(): number {
    if (!this.data.length || !this.config.values.length) {
      return (this.config.maxValue || 10000);
    }
    const dataMaxValue = this.data.reduce((maxValue: number, dataItem: DataItem) => {
      this.config.values.forEach((valueId: string) => {
        maxValue = Math.max(max(dataItem.values[valueId]), maxValue);
      });
      return maxValue;
    }, 0);
    return Math.max((Math.ceil(dataMaxValue / 100) * 100), (this.config.maxValue || 1));
  }

  private renderAxis(transitionsDurationInMillis = this.config.transitionsDurationInMillis): void {
    let axisGroup = this.element
      .select(`.${ AreaChartComponent.COMPONENT_CLASS }__axis-group`);
    if (axisGroup.empty()) {
      axisGroup = this.initializeAxis();
    }
    axisGroup
      .selectAll(`.${ AreaChartComponent.COMPONENT_CLASS }__axis-line--y`)
      .attr('x1', this.scaleX.range()[0])
      .attr('y1', (datum, index: number) => this.scaleY(this.getAxisValueY(index)))
      .attr('x2', this.size.width)
      .attr('y2', (datum, index: number) => this.scaleY(this.getAxisValueY(index)));
    axisGroup
      .selectAll(`.${ AreaChartComponent.COMPONENT_CLASS }__axis-text--y`)
      .attr('x', this.scaleX.range()[0])
      .attr('y', (datum, index: number) => this.scaleY(this.getAxisValueY(index)))
      .attr('dx', -(AreaChartComponent.POINT_RADIUS * 2))
      .text((datum, index: number) => this.getAxisTextY(index));
    axisGroup
      .selectAll(`.${ AreaChartComponent.COMPONENT_CLASS }__axis-line--x`)
      .attr('x1', this.scaleX.range()[0])
      .attr('y1', this.scaleY.range()[0])
      .attr('x2', this.scaleX.range()[0])
      .attr('y2', 0);
    axisGroup
      .selectAll(`.${ AreaChartComponent.COMPONENT_CLASS }__axis-line--tooltip`)
      .attr('class', this.getTooltipAxisLineClass());
    this.renderAxisData(axisGroup, transitionsDurationInMillis);
  }

  private initializeAxis() {
    const axisGroup = this.element
      .append('g')
      .attr('class', `${ AreaChartComponent.COMPONENT_CLASS }__axis-group`);
    for (let index = 0; index <= AreaChartComponent.Y_AXIS_STEPS; index++) {
      axisGroup
        .append('line')
        .attr('class', `${ AreaChartComponent.COMPONENT_CLASS }__axis-line ${ AreaChartComponent.COMPONENT_CLASS }__axis-line--y`)
        .style('opacity', 0)
        .transition().duration(this.config.transitionsDurationInMillis)
        .style('opacity', 1);
      axisGroup
        .append('text')
        .attr('class', `${ AreaChartComponent.COMPONENT_CLASS }__axis-text ${ AreaChartComponent.COMPONENT_CLASS }__axis-text--y`)
        .style('opacity', 0)
        .transition().duration(this.config.transitionsDurationInMillis)
        .style('opacity', 1);
    }
    axisGroup
      .append('line')
      .attr('class', `${ AreaChartComponent.COMPONENT_CLASS }__axis-line ${ AreaChartComponent.COMPONENT_CLASS }__axis-line--x`)
      .style('opacity', 0)
      .transition().duration(this.config.transitionsDurationInMillis)
      .style('opacity', 1);
    axisGroup
      .append('line')
      .attr('class', `${ AreaChartComponent.COMPONENT_CLASS }__axis-line ${ AreaChartComponent.COMPONENT_CLASS }__axis-line--x
      ${ AreaChartComponent.COMPONENT_CLASS }__axis-line--tooltip`)
      .style('opacity', 0);
    return axisGroup;
  }

  private getAxisValueY(index: number): number {
    return ((this.getDataMaxValue() / AreaChartComponent.Y_AXIS_STEPS) * index);
  }

  private getAxisTextY(index: number): string {
    let value = this.getAxisValueY(index);
    let valueUnits = (this.config.valueUnits || '');
    if (Math.floor(value / 1000000) > 0) {
      value /= 1000000;
      valueUnits = `m${ valueUnits }`;
    } else if (Math.floor(value / 1000) > 0) {
      value /= 1000;
      valueUnits = `k${ valueUnits }`;
    }
    const valueText = value.toLocaleString(this.config.locale, {
      maximumFractionDigits: 1
    });
    return `${ valueText }${ valueUnits }`;
  }

  private getTooltipAxisLineClass(): string {
    const baseCls = `${ AreaChartComponent.COMPONENT_CLASS }__axis-line`;
    let cls = `${ baseCls } ${ baseCls }--x ${ baseCls }--tooltip`;
    if (this.config.values.length === 1) {
      cls += ` ${ baseCls }--${ this.config.values[0] }`;
    }
    return cls;
  }

  private renderAxisData(axisGroup: Selection, transitionsDurationInMillis: number): void {
    const series = axisGroup
      .selectAll(`.${ AreaChartComponent.COMPONENT_CLASS }__axis-serie`)
      .data(this.data, (dataItem: DataItem) => dataItem.id);
    this.removeAxisSeries(series, transitionsDurationInMillis);
    this.updateAxisSeries(series, transitionsDurationInMillis);
    this.enterAxisSeries(series, transitionsDurationInMillis);
  }

  private removeAxisSeries(series: Selection, transitionsDurationInMillis: number): void {
    const exitSeries = series.exit();
    exitSeries
      .transition().duration(transitionsDurationInMillis)
      .style('opacity', 0);
    exitSeries
      .transition().delay(transitionsDurationInMillis)
      .remove();
  }

  private updateAxisSeries(series: Selection, transitionsDurationInMillis: number): void {
    series
      .transition().duration(transitionsDurationInMillis)
      .style('opacity', 1);
    series
      .select(`.${ AreaChartComponent.COMPONENT_CLASS }__axis-text--x`)
      .attr('y', this.scaleY(0))
      .transition().duration(transitionsDurationInMillis)
      .attr('x', (dataItem: DataItem) => this.scaleX(dataItem.id))
      .style('opacity', (dataItem: DataItem) => this.getAxisSerieTextOpacity(dataItem));
  }

  private getAxisSerieTextOpacity(dataItem: DataItem): number {
    const indexOfDataItem = this.data.findIndex((item: DataItem) => (item.id === dataItem.id));
    const chartWidth = (this.scaleX.range()[1] - this.scaleX.range()[0]);
    const denominator = (Math.floor(this.data.length / (chartWidth / AreaChartComponent.AXIS_TEXT_WIDTH)) + 1);
    return (((indexOfDataItem % denominator) === 0) ? 1 : 0);
  }

  private enterAxisSeries(series: Selection, transitionsDurationInMillis: number): void {
    const enterSeries = series.enter()
      .append('g')
      .attr('class', `${ AreaChartComponent.COMPONENT_CLASS }__axis-serie`);
    enterSeries
      .style('opacity', 0)
      .transition().duration(transitionsDurationInMillis)
      .style('opacity', 1);
    enterSeries
      .append('text')
      .attr('class', `${ AreaChartComponent.COMPONENT_CLASS }__axis-text ${ AreaChartComponent.COMPONENT_CLASS }__axis-text--x`)
      .attr('x', (dataItem: DataItem) => this.scaleX(dataItem.id))
      .attr('y', this.scaleY.range()[0])
      .attr('dy', AreaChartComponent.POINT_RADIUS)
      .text((dataItem: DataItem) => dataItem.labels[0])
      .style('opacity', (dataItem: DataItem) => this.getAxisSerieTextOpacity(dataItem));
  }

  private renderData(transitionsDurationInMillis = this.config.transitionsDurationInMillis): void {
    const series = this.element
      .selectAll(`.${ AreaChartComponent.COMPONENT_CLASS }__value-serie`)
      .data(this.config.values, (valueId: string) => valueId);
    this.removeValueSeries(series, transitionsDurationInMillis);
    this.updateValueSeries(series, transitionsDurationInMillis);
    this.enterValueSeries(series, transitionsDurationInMillis);
    series.order();
  }

  private removeValueSeries(series: Selection, transitionsDurationInMillis: number): void {
    const exitSeries = series.exit();
    exitSeries
      .transition().duration(transitionsDurationInMillis)
      .style('opacity', 0);
    exitSeries
      .transition().delay(transitionsDurationInMillis)
      .remove();
  }

  private updateValueSeries(series: Selection, transitionsDurationInMillis: number): void {
    series
      .transition().duration(transitionsDurationInMillis)
      .style('opacity', 1);
    const thisValue = this;
    series.each(function () {
      thisValue.renderIndexSeries(select(this), transitionsDurationInMillis);
    });
  }

  private enterValueSeries(series: Selection, transitionsDurationInMillis: number): void {
    const valueSerieCls = `${ AreaChartComponent.COMPONENT_CLASS }__value-serie`;
    const enterSeries = series.enter()
      .append('g')
      .attr('class', (valueId: string) => `${ valueSerieCls } ${ valueSerieCls }--${ valueId }`);
    enterSeries
      .style('opacity', 0)
      .transition().duration(transitionsDurationInMillis)
      .style('opacity', 1);
    const thisValue = this;
    enterSeries.each(function () {
      thisValue.renderIndexSeries(select(this), transitionsDurationInMillis, false);
    });
  }

  private renderIndexSeries(valueSerie: Selection, transitionsDurationInMillis: number, fade = true): void {
    const series = valueSerie
      .selectAll(`.${ AreaChartComponent.COMPONENT_CLASS }__index-serie`)
      .data(this.getValueDataByIndex(valueSerie.datum()), (indexData: IndexDataItem[], index: number) => index);
    this.removeIndexSeries(series, transitionsDurationInMillis, fade);
    this.updateIndexSeries(series, transitionsDurationInMillis, fade);
    this.enterIndexSeries(series, transitionsDurationInMillis, fade);
  }

  private getValueDataByIndex(valueId: string): IndexDataItem[][] {
    const dataByIndex = [];
    if (!!this.data.length) {
      this.data[0].values[valueId].forEach((value: number, index: number) => {
        dataByIndex.push(this.data.map((dataItem: DataItem) => ({
          id: dataItem.id,
          label: dataItem.labels[index],
          value: dataItem.values[valueId][index]
        })));
      });
    }
    return dataByIndex;
  }

  private removeIndexSeries(series: Selection, transitionsDurationInMillis: number, fade: boolean): void {
    const exitSeries = series.exit();
    if (fade) {
      exitSeries
        .transition().duration(transitionsDurationInMillis)
        .style('opacity', 0);
    }
    exitSeries
      .transition().delay(transitionsDurationInMillis)
      .remove();
  }

  private updateIndexSeries(series: Selection, transitionsDurationInMillis: number, fade: boolean): void {
    series
      .transition().duration(transitionsDurationInMillis)
      .style('opacity', (indexData: IndexDataItem[], index: number) => this.getIndexSerieOpacity(index));
    series
      .select(`.${ AreaChartComponent.COMPONENT_CLASS }__data-area`)
      .transition().duration(transitionsDurationInMillis)
      .attr('d', (indexData: IndexDataItem[]) => this.getDataAreaCommands(indexData));
    series
      .select(`.${ AreaChartComponent.COMPONENT_CLASS }__data-line`)
      .transition().duration(transitionsDurationInMillis)
      .attr('d', (indexData: IndexDataItem[]) => this.getDataLineCommands(indexData));
    const thisValue = this;
    series.each(function () {
      thisValue.renderItemSeries(select(this), transitionsDurationInMillis, fade);
    });
  }

  private getIndexSerieOpacity(index: number): number {
    return ((index === 0) ? 1 : 0.5);
  }

  private getDataAreaCommands(indexData: IndexDataItem[]): string {
    return area()
      .curve(curveMonotoneX)
      .x((dataItem: IndexDataItem) => this.scaleX(dataItem.id))
      .y0(this.scaleY.range()[0])
      .y1((dataItem: IndexDataItem) => this.scaleY(dataItem.value))
      (indexData);
  }

  private getDataLineCommands(indexData: IndexDataItem[]): string {
    return line()
      .curve(curveMonotoneX)
      .x((dataItem: IndexDataItem) => this.scaleX(dataItem.id))
      .y((dataItem: IndexDataItem) => this.scaleY(dataItem.value))
      (indexData);
  }

  private renderItemSeries(indexSerie: Selection, transitionsDurationInMillis: number, fade: boolean): void {
    const series = indexSerie
      .selectAll(`.${ AreaChartComponent.COMPONENT_CLASS }__item-serie`)
      .data(indexSerie.datum(), (dataItem: IndexDataItem) => dataItem.id);
    this.removeItemSeries(series, transitionsDurationInMillis, fade);
    this.updateItemSeries(series, transitionsDurationInMillis, fade);
    this.enterItemSeries(series, transitionsDurationInMillis, fade);
  }

  private removeItemSeries(series: Selection, transitionsDurationInMillis: number, fade: boolean): void {
    const exitSeries = series.exit();
    if (fade) {
      exitSeries
        .transition().duration(transitionsDurationInMillis)
        .style('opacity', 0);
    }
    exitSeries
      .transition().delay(transitionsDurationInMillis)
      .remove();
  }

  private updateItemSeries(series: Selection, transitionsDurationInMillis: number, fade: boolean): void {
    series
      .transition().duration(transitionsDurationInMillis)
      .style('opacity', 1);
    series
      .select(`.${ AreaChartComponent.COMPONENT_CLASS }__data-point`)
      .transition().duration(transitionsDurationInMillis)
      .attr('cx', (dataItem: IndexDataItem) => this.scaleX(dataItem.id))
      .attr('cy', (dataItem: IndexDataItem) => this.scaleY(dataItem.value))
      .attr('r', AreaChartComponent.POINT_RADIUS);
  }

  private enterItemSeries(series: Selection, transitionsDurationInMillis: number, fade: boolean): void {
    const enterSeries = series.enter()
      .append('g')
      .attr('class', `${ AreaChartComponent.COMPONENT_CLASS }__item-serie`);
    if (fade) {
      enterSeries
        .style('opacity', 0)
        .transition().duration(transitionsDurationInMillis)
        .style('opacity', 1);
    }
    enterSeries
      .append('circle')
      .attr('class', `${ AreaChartComponent.COMPONENT_CLASS }__data-point`)
      .attr('cx', (dataItem: IndexDataItem) => this.scaleX(dataItem.id))
      .attr('cy', this.scaleY.range()[0])
      .transition().duration(transitionsDurationInMillis)
      .attr('cy', (dataItem: IndexDataItem) => this.scaleY(dataItem.value))
      .attr('r', AreaChartComponent.POINT_RADIUS);
  }

  private enterIndexSeries(series: Selection, transitionsDurationInMillis: number, fade: boolean): void {
    const enterSeries = series.enter()
      .append('g')
      .attr('class', `${ AreaChartComponent.COMPONENT_CLASS }__index-serie`);
    enterSeries
      .style('opacity', 0)
      .transition().duration((fade ? transitionsDurationInMillis : 0))
      .style('opacity', (indexData: IndexDataItem[], index: number) => this.getIndexSerieOpacity(index));
    enterSeries
      .append('path')
      .attr('class', `${ AreaChartComponent.COMPONENT_CLASS }__data-area`)
      .attr('d', this.getDataPathInitialCommands())
      .transition().duration(transitionsDurationInMillis)
      .attr('d', (indexData: IndexDataItem[]) => this.getDataAreaCommands(indexData));
    enterSeries
      .append('path')
      .attr('class', `${ AreaChartComponent.COMPONENT_CLASS }__data-line`)
      .attr('d', this.getDataPathInitialCommands())
      .transition().duration(transitionsDurationInMillis)
      .attr('d', (indexData: IndexDataItem[]) => this.getDataLineCommands(indexData));
    const thisValue = this;
    enterSeries.each(function () {
      thisValue.renderItemSeries(select(this), transitionsDurationInMillis, false);
    });
  }

  private getDataPathInitialCommands(): string {
    return line()
      .curve(curveMonotoneX)
      .x((dataItem: DataItem) => this.scaleX(dataItem.id))
      .y(this.scaleY.range()[0])
      (this.data);
  }

  private renderTooltip(): void {
    if (!!this.data.length && !!this.config.values.length) {
      this.element
        .on('mouseover', () => this.onElementMouseOver())
        .on('mousemove', (event: MouseEvent) => this.onElementMouseMove(pointer(event)))
        .on('mouseout', () => this.onElementMouseOut());
    } else {
      this.element
        .on('mouseover mousemove mouseout', undefined);
    }
    this.element
      .select(`.${ AreaChartComponent.COMPONENT_CLASS }__tooltip`)
      .remove();
    const tooltipContent = this.element
      .append('foreignObject')
      .attr('class', this.getTooltipClass())
      .style('opacity', 0)
      .append('xhtml:div')
      .attr('class', `${ AreaChartComponent.COMPONENT_CLASS }__tooltip-content-wrapper`)
      .append('div')
      .attr('class', `${ AreaChartComponent.COMPONENT_CLASS }__tooltip-content`);
    if (!!this.data.length) {
      this.data[0].labels.forEach((label: string) => {
        const tooltipContentGroup = tooltipContent
          .append('div')
          .attr('class', `${ AreaChartComponent.COMPONENT_CLASS }__tooltip-content-group`);
        const tooltipContentTextCls = `${ AreaChartComponent.COMPONENT_CLASS }__tooltip-content-text`;
        tooltipContentGroup
          .append('span')
          .attr('class', `${ tooltipContentTextCls } ${ tooltipContentTextCls }--label`);
        this.config.values.forEach((valueId: string) => {
          tooltipContentGroup
            .append('span')
            .attr('class', `${ tooltipContentTextCls } ${ tooltipContentTextCls }--${ valueId }`);
        });
      });
    }
  }

  private onElementMouseOver(): void {
    this.element
      .select(`.${ AreaChartComponent.COMPONENT_CLASS }__axis-line--tooltip`)
      .transition().duration(AreaChartComponent.TOOLTIP_TRANSITIONS_DURATION_IN_MILLIS)
      .style('opacity', 1);
    this.element
      .select(`.${ AreaChartComponent.COMPONENT_CLASS }__tooltip`)
      .transition().duration(AreaChartComponent.TOOLTIP_TRANSITIONS_DURATION_IN_MILLIS)
      .style('opacity', 1);
  }

  private onElementMouseOut(): void {
    this.element
      .select(`.${ AreaChartComponent.COMPONENT_CLASS }__axis-line--tooltip`)
      .transition().duration(AreaChartComponent.TOOLTIP_TRANSITIONS_DURATION_IN_MILLIS)
      .style('opacity', 0);
    this.element
      .select(`.${ AreaChartComponent.COMPONENT_CLASS }__tooltip`)
      .transition().duration(AreaChartComponent.TOOLTIP_TRANSITIONS_DURATION_IN_MILLIS)
      .style('opacity', 0);
  }

  private onElementMouseMove(mousePosition: number[]): void {
    const scaleRangeX = this.scaleX.range();
    const scaleStepX = this.scaleX.step();
    const rangeArray: number[] = range((scaleRangeX[0] - (scaleStepX / 2)), (scaleRangeX[1] + (scaleStepX / 2)), scaleStepX);
    const hoveredDataItem = this.data[bisect(rangeArray, mousePosition[0]) - 1];
    if (!!hoveredDataItem) {
      const tooltipPositionX = this.scaleX(hoveredDataItem.id);
      this.element
        .select(`.${ AreaChartComponent.COMPONENT_CLASS }__axis-line--tooltip`)
        .attr('x1', tooltipPositionX)
        .attr('x2', tooltipPositionX);
      this.element
        .select(`.${ AreaChartComponent.COMPONENT_CLASS }__tooltip-content`)
        .style('transform', `translate3d(${ this.getTooltipContentTranslateX(hoveredDataItem) }, 0, 0)`);
      this.element
        .selectAll(`.${ AreaChartComponent.COMPONENT_CLASS }__tooltip-content-text--label`)
        .text((datum, index: number) => hoveredDataItem.labels[index]);
      this.config.values.forEach((valueId: string) => {
        this.element
          .selectAll(`.${ AreaChartComponent.COMPONENT_CLASS }__tooltip-content-text--${ valueId }`)
          .text((datum, index: number) => this.getDataItemValueText(hoveredDataItem.values[valueId][index]));
      });
    }
  }

  private getTooltipContentTranslateX(hoveredDataItem: DataItem): string {
    const positionX = this.scaleX(hoveredDataItem.id);
    const offsetX = (AreaChartComponent.POINT_RADIUS * 2);
    if (this.data.findIndex((dataItem: DataItem) => (dataItem.id === hoveredDataItem.id)) > (this.data.length / 2)) {
      return `calc(${ (positionX - offsetX) }px - 100%)`;
    }
    return `${ (positionX + offsetX) }px`;
  }

  private getDataItemValueText(value: number): string {
    let valueText = value.toLocaleString(this.config.locale);
    if (!!this.config.valueUnits) {
      valueText += this.config.valueUnits;
    }
    return valueText;
  }

  private getTooltipClass(): string {
    const baseCls = `${ AreaChartComponent.COMPONENT_CLASS }__tooltip`;
    let cls = `${ baseCls }`;
    if (this.config.values.length === 1) {
      cls += ` ${ baseCls }--${ this.config.values[0] }`;
    } else {
      cls += ` ${ baseCls }--multivalue`;
    }
    return cls;
  }

}

export interface Config extends BaseConfig {
  values: string[];
  maxValue?: number;
  valueUnits?: string;
}

export type Data = DataItem[];

export interface DataItem {
  id: string;
  labels: string[];
  values: Dictionary<number[]>;
}

interface IndexDataItem {
  id: string;
  label: string;
  value: number;
}
